import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import LeadForm from "@/components/LeadForm";
import MainHeader from "@/components/MainHeader";
import LoanProgramPage from "@/pages/LoanProgramPage";
import LoanOfficerPage from "@/pages/LoanOfficerPage";
import LeadPopup from "@/components/LeadPopup";
import RealEstatePage from "@/pages/RealEstatePage";
import BuyAHomePage from "@/pages/BuyAHomePage";
import MortgageCalculatorPage from "@/pages/MortgageCalculatorPage";
import RefinancePage from "@/pages/RefinancePage";
import RefinanceThanksPage from "@/pages/RefinanceThanksPage";
import FirstTimeHomebuyerGuidePage from "@/pages/FirstTimeHomebuyerGuidePage";
import BlogIndexPage from "@/pages/BlogIndexPage";
import BlogPostPage from "@/pages/BlogPostPage";
import LoginPage from "@/pages/LoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminArticleEditorPage from "@/pages/AdminArticleEditorPage";
import SEO from "@/components/SEO";
import ReviewsSection from "@/components/ReviewsSection";
import FamilyBanner from "@/components/FamilyBanner";
import { breadcrumbJsonLd, buildGraph, webPageJsonLd, faqJsonLd } from "@/data/seo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  PhoneIncoming,
  FileCheck,
  Search,
  ShieldCheck,
  Users,
  TrendingUp,
  Home,
  Clock,
  CheckCircle,
  Heart,
  ArrowRight,
  Zap,
  Shield,
  Sparkles,
  Target,
  Award,
  MessageCircle,
  Play,
  ChevronRight,
  Star,
  Building,
  Calculator,
  FileText,
  Handshake,
  Phone,
  Mail,
  MessageSquare,
  ThumbsUp,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_happy-homes-ahead/artifacts/0buyd3la_CW%20Logo%20%281%29.svg";
const PRE_APPROVAL_LINK = "https://cityworth.loanzify.io/register";

// ============================================
// SHARED COMPONENTS (Header & Footer)
// ============================================

// Header Component
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100" data-testid="header">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <a href="/" data-testid="logo-link">
          <img 
            src={LOGO_URL} 
            alt="CityWorth" 
            className="h-10 md:h-12 w-auto"
            data-testid="logo-image"
          />
        </a>
        <Button 
          className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-6 py-2 text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          onClick={() => window.open(PRE_APPROVAL_LINK, '_blank')}
          data-testid="header-cta-btn"
        >
          Get Pre-Approved
        </Button>
      </div>
    </header>
  );
};

// Footer Section
const Footer = () => {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  return (
    <footer className="py-20 bg-gray-900" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Company Info & Links */}
        <div className="pt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
            {/* Logo & Address */}
            <div>
              <img 
                src={LOGO_URL} 
                alt="CityWorth" 
                className="h-10 w-auto mb-4"
                data-testid="footer-logo"
              />
              <p className="text-gray-400 text-sm leading-relaxed">
                11781 Lee Jackson Hwy Ste 100<br />
                Fairfax, VA 22033
              </p>
              <p className="text-gray-500 text-sm mt-2">
                NMLS# 925476
              </p>
            </div>
            
            {/* Legal Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <button 
                    onClick={() => setShowPrivacy(true)} 
                    className="text-gray-400 hover:text-white text-sm transition-colors text-left" 
                    data-testid="privacy-link"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => setShowTerms(true)} 
                    className="text-gray-400 hover:text-white text-sm transition-colors text-left" 
                    data-testid="terms-link"
                  >
                    Terms of Service
                  </button>
                </li>
                <li>
                  <a href="https://www.nmlsconsumeraccess.org/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="nmls-link">
                    NMLS Consumer Access
                  </a>
                </li>
              </ul>
            </div>
            
            {/* Equal Housing */}
            <div>
              <h4 className="text-white font-semibold mb-4">Equal Housing Lender</h4>
              <div className="flex items-start gap-3">
                <Home className="w-8 h-8 text-gray-400 flex-shrink-0" />
                <p className="text-gray-500 text-xs leading-relaxed">
                  We are committed to the letter and spirit of U.S. policy for the achievement of equal housing opportunity throughout the Nation.
                </p>
              </div>
            </div>
          </div>
          
          {/* Compliance Disclosures */}
          <div className="border-t border-gray-800 pt-8">
            <p className="text-gray-500 text-xs leading-relaxed mb-4">
              CityWorth Mortgage LLC is a mortgage lender (NMLS# 925476). This is not a commitment to lend. Programs, rates, terms and conditions are subject to change without notice. All loans subject to credit approval. Equal Housing Lender.
            </p>
            <p className="text-gray-500 text-xs leading-relaxed mb-4">
              The content on this website is for informational purposes only and does not constitute an offer to sell, a solicitation of an offer to buy, or a recommendation for any security, or as an offer to provide advisory or other services in any jurisdiction.
            </p>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-4">
              <p className="text-gray-500 text-sm">
                © {new Date().getFullYear()} CityWorth Mortgage LLC. All rights reserved.
              </p>
              <p className="text-gray-600 text-xs">
                NMLS# 925476 | Equal Housing Lender
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <Dialog open={showPrivacy} onOpenChange={setShowPrivacy}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl">Privacy Policy</DialogTitle>
            <DialogDescription className="sr-only">CityWorth Mortgage LLC Privacy Policy document</DialogDescription>
          </DialogHeader>
          <div className="prose prose-sm max-w-none text-gray-600 space-y-4">
            <p className="text-sm text-gray-500">Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Introduction</h3>
            <p>CityWorth Mortgage LLC ("CityWorth," "we," "us," or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Information We Collect</h3>
            <p>We may collect personal information that you voluntarily provide to us when you:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Complete a mortgage application or pre-approval request</li>
              <li>Contact us via phone, email, or our website</li>
              <li>Subscribe to our newsletter or marketing communications</li>
              <li>Participate in surveys or promotions</li>
            </ul>
            <p>This information may include your name, address, email, phone number, Social Security number, income information, employment details, and other financial information necessary to process your mortgage application.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">How We Use Your Information</h3>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Process and evaluate your mortgage application</li>
              <li>Communicate with you about your loan status</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Improve our services and customer experience</li>
              <li>Send you relevant marketing communications (with your consent)</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-gray-900">Information Sharing</h3>
            <p>We may share your information with:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Lenders, investors, and other parties involved in processing your loan</li>
              <li>Credit bureaus and verification services</li>
              <li>Government agencies as required by law</li>
              <li>Service providers who assist in our operations</li>
            </ul>
            <p>We do not sell your personal information to third parties for marketing purposes.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Data Security</h3>
            <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Your Rights</h3>
            <p>You have the right to access, correct, or delete your personal information. To exercise these rights or if you have questions about this Privacy Policy, please contact us at our office address.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Updates to This Policy</h3>
            <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Terms of Service Modal */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl">Terms of Service</DialogTitle>
            <DialogDescription className="sr-only">CityWorth Mortgage LLC Terms of Service document</DialogDescription>
          </DialogHeader>
          <div className="prose prose-sm max-w-none text-gray-600 space-y-4">
            <p className="text-sm text-gray-500">Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Agreement to Terms</h3>
            <p>By accessing or using the CityWorth Mortgage LLC website and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Services Description</h3>
            <p>CityWorth Mortgage LLC provides mortgage lending services, including but not limited to mortgage pre-approval, loan origination, and related financial services. Our services are subject to qualification requirements and approval.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Eligibility</h3>
            <p>To use our services, you must be at least 18 years old and legally capable of entering into binding contracts. By using our services, you represent that you meet these requirements.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Accuracy of Information</h3>
            <p>You agree to provide accurate, current, and complete information when applying for our services. Providing false or misleading information may result in denial of your application and may constitute fraud.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">No Guarantee of Approval</h3>
            <p>Submitting an application or pre-approval request does not guarantee loan approval. All loans are subject to credit approval, underwriting requirements, and other conditions.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Intellectual Property</h3>
            <p>All content on this website, including text, graphics, logos, and images, is the property of CityWorth Mortgage LLC and is protected by copyright and trademark laws.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Limitation of Liability</h3>
            <p>CityWorth Mortgage LLC shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our website or services. Our total liability shall not exceed the amount of fees paid by you for our services.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Third-Party Links</h3>
            <p>Our website may contain links to third-party websites. We are not responsible for the content or privacy practices of these external sites.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Modifications</h3>
            <p>We reserve the right to modify these Terms of Service at any time. Your continued use of our services after any changes constitutes acceptance of the modified terms.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Governing Law</h3>
            <p>These Terms of Service shall be governed by and construed in accordance with the laws of the United States and applicable state laws.</p>
            
            <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
            <p>If you have questions about these Terms of Service, please contact us at:</p>
            <p>CityWorth Mortgage LLC<br />11781 Lee Jackson Hwy Ste 100<br />Fairfax, VA 22033</p>
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
};

// ============================================
// HOME PAGE - Light theme, service-focused
// ============================================

const HomePage = () => {
  return (
    <>
      <SEO
        title="All-in-one homebuying, simplified"
        description="CityWorth Mortgage delivers mortgage and real estate in one team. From home searching to loan approval, guided, fast, and simple. NMLS #925476."
        path="/"
        jsonLd={buildGraph(
          webPageJsonLd({
            name: "CityWorth Mortgage | All-in-one homebuying",
            description: "Mortgage and real estate in one team. A simple, guided path from home searching to loan approval.",
            path: "/",
          }),
          breadcrumbJsonLd([{ name: "Home", path: "/" }])
        )}
      />
      {/* Hero Section - All-in-One focus */}
      <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 overflow-hidden bg-white" data-testid="home-hero">
        {/* Subtle background accents */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-50 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-red-50 rounded-full blur-3xl opacity-40 translate-y-1/2 -translate-x-1/3 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left, copy */}
            <div className="lg:col-span-7 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-red-50 rounded-full px-4 py-2 mb-8 animate-fade-in-up">
                <Sparkles className="w-4 h-4 text-[#D41F30]" />
                <span className="text-xs font-bold tracking-[0.18em] text-[#D41F30] uppercase">
                  All-in-One Homebuying
                </span>
              </div>

              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.02] tracking-tight mb-6 animate-fade-in-up animation-delay-100">
                Your homebuying journey,
                <span className="block text-[#D41F30]">simplified.</span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-10 max-w-xl mx-auto lg:mx-0 animate-fade-in-up animation-delay-200">
                Mortgage and real estate, all handled under one roof at CityWorth. No juggling multiple companies. No surprises. Just a simple, guided path from home searching to loan approval.
              </p>

              {/* All-in-one chips */}
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start mb-10 animate-fade-in-up animation-delay-300">
                {[
                  { label: "Mortgage", Icon: Calculator },
                  { label: "Real Estate", Icon: Home },
                ].map(({ label, Icon }) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm"
                    data-testid={`hero-chip-${label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className="w-4 h-4 text-[#D41F30]" />
                    {label}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-400">
                <Button
                  className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-6 py-3.5 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
                  onClick={() => window.location.assign('/get-started')}
                  data-testid="home-hero-cta"
                >
                  Start my homebuying plan
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  variant="outline"
                  className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full px-6 py-3.5 text-base font-semibold transition-all duration-300"
                  onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                  data-testid="home-learn-more"
                >
                  See how it works
                </Button>
              </div>

              {/* Trust line */}
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 justify-center lg:justify-start text-sm text-gray-500 animate-fade-in-up animation-delay-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>No upfront cost</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Takes 2 minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Won't affect your credit</span>
                </div>
              </div>
            </div>

            {/* Right, visual */}
            <div className="lg:col-span-5 relative animate-fade-in-up animation-delay-300">
              <div className="relative w-full">
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="/img/25je1t4m.webp"
                    alt="Happy couple looking at homes for sale"
                    className="w-full h-[480px] object-cover"
                  />
                </div>

                {/* "Under one roof" card, top-right */}
                <div className="hidden md:block absolute -top-5 -right-5 bg-white rounded-2xl shadow-2xl px-5 py-4 border border-gray-100 max-w-[230px]">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                      <Home className="w-4 h-4 text-[#D41F30]" />
                    </div>
                    <p className="font-bold text-gray-900 text-sm leading-tight">
                      Under one roof
                    </p>
                  </div>
                  <ul className="space-y-1.5 text-xs text-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      Lender
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      Real estate agent
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      Concierge support
                    </li>
                  </ul>
                </div>

                {/* Simplicity stat, bottom-left */}
                <div className="absolute -bottom-6 -left-4 md:-left-6 bg-white rounded-2xl shadow-xl p-5 border border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#D41F30] rounded-xl flex items-center justify-center">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-display text-3xl font-black text-gray-900 leading-none">21 days</p>
                      <p className="text-xs text-gray-500 mt-1">Average close time</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50 border-b border-gray-100" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { value: "$1.7B+", label: "Loans Funded", icon: Building },
              { value: "10,000+", label: "Happy Customers", icon: Users },
              { value: "4.8/5", label: "Customer Rating", icon: Star },
            ].map((stat, index) => (
              <div key={index} className="text-center group" data-testid={`stat-${index}`}>
                <div className="w-14 h-14 mx-auto mb-4 bg-red-50 rounded-2xl flex items-center justify-center group-hover:bg-[#D41F30] transition-colors duration-300">
                  <stat.icon className="w-7 h-7 text-[#D41F30] group-hover:text-white transition-colors duration-300" />
                </div>
                <p className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">{stat.value}</p>
                <p className="text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 bg-gray-50" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-red-100 rounded-full px-4 py-2 mb-6">
              <Zap className="w-4 h-4 text-[#D41F30]" />
              <span className="text-sm font-medium text-[#D41F30]">Simple Process</span>
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How CityWorth Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From first click to keys in hand, we've streamlined every step
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                icon: Calculator,
                title: "Get Pre-Approved",
                description: "Quick online application takes just minutes. Know your budget instantly.",
                color: "bg-blue-500"
              },
              {
                step: "02",
                icon: Search,
                title: "Find Your Home",
                description: "Our agents help you discover properties that match your dreams.",
                color: "bg-green-500"
              },
              {
                step: "03",
                icon: FileText,
                title: "Lock Your Rate",
                description: "We secure the best rates and handle all the paperwork for you.",
                color: "bg-purple-500"
              },
              {
                step: "04",
                icon: Home,
                title: "Close & Celebrate",
                description: "Get your keys and move into your new home. We handle the rest.",
                color: "bg-[#D41F30]"
              }
            ].map((item, index) => (
              <div key={index} className="relative group" data-testid={`step-${index}`}>
                <div className="bg-white rounded-3xl p-8 h-full shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className={`w-14 h-14 ${item.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-6xl font-bold text-gray-100 absolute top-6 right-6">{item.step}</span>
                  <h3 className="font-playfair text-xl font-semibold text-gray-900 mb-3 relative">{item.title}</h3>
                  <p className="text-gray-600 relative">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button 
              className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              onClick={() => window.open(PRE_APPROVAL_LINK, '_blank')}
              data-testid="how-it-works-cta"
            >
              Start Your Journey
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why CityWorth Section */}
      <section className="py-24 bg-white" data-testid="why-cityworth">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-100 rounded-full px-4 py-2 mb-6">
                <Award className="w-4 h-4 text-[#D41F30]" />
                <span className="text-sm font-medium text-[#D41F30]">Why Choose Us</span>
              </div>
              <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                The CityWorth <span className="text-[#D41F30]">Advantage</span>
              </h2>
              <p className="text-xl text-gray-600 mb-10">
                We're not just another mortgage company. We're your partners in making homeownership simple, affordable, and stress-free.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    icon: Users,
                    title: "One Dedicated Team",
                    description: "Your real estate agent and mortgage expert work together seamlessly"
                  },
                  {
                    icon: Zap,
                    title: "Lightning Fast Process",
                    description: "Close in as little as 21 days with our streamlined approach"
                  },
                  {
                    icon: Shield,
                    title: "Transparent Pricing",
                    description: "No hidden fees, no surprises. Know exactly what you're paying"
                  },
                  {
                    icon: Heart,
                    title: "Personalized Guidance",
                    description: "We walk you through every step, answering all your questions"
                  }
                ].map((feature, index) => (
                  <div key={index} className="flex gap-4 p-4 rounded-2xl hover:bg-gray-50 transition-colors duration-300" data-testid={`advantage-${index}`}>
                    <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-[#D41F30]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img loading="lazy" 
                  src="/img/pxyj2imd.webp"
                  alt="Modern home interior"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6">
                    <div className="flex items-center gap-4">
                      <div className="flex -space-x-3">
                        <div className="w-10 h-10 bg-[#D41F30] rounded-full flex items-center justify-center text-white font-semibold">J</div>
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">S</div>
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold">M</div>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Join 10,000+ Happy Customers</p>
                        <p className="text-sm text-gray-500">Who made their dreams come true</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FamilyBanner testId="family-banner-home" />
      <ReviewsSection testId="testimonials" />

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#D41F30] to-red-700 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full filter blur-[128px]"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-[128px]"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-playfair text-4xl md:text-6xl font-bold text-white mb-6">
            Ready to Find Your Dream Home?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Get pre-approved in minutes and start your journey to homeownership today. 
            No obligations, no hard credit pull.
          </p>
          <Button 
            className="bg-white text-[#D41F30] hover:bg-gray-100 rounded-full px-12 py-7 text-xl font-semibold shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105"
            onClick={() => window.open(PRE_APPROVAL_LINK, '_blank')}
            data-testid="final-cta"
          >
            Get Pre-Approved Now
            <ArrowRight className="ml-3 w-6 h-6" />
          </Button>
          <p className="mt-6 text-white/60 text-sm">
            Takes only 15 minutes • No impact on credit score
          </p>
        </div>
      </section>
    </>
  );
};

// ============================================
// HOMEBUYER LANDING PAGE (post-form thank-you)
// Mirrors HomePage but with call/text CTAs.
// ============================================

const TEAM_PHONE = "571-520-8688";
const TEAM_PHONE_TEL = "tel:+15715208688";
const TEAM_PHONE_SMS = "sms:+15715208688";

const HomebuyerLandingPage = () => {
  return (
    <>
      <SEO
        title="Your homebuying plan | Talk to your CityWorth team"
        description="Your CityWorth team is ready to help. Call or text 571-520-8688 to get personalized homebuying guidance."
        path="/homebuyer"
        noindex
      />

      {/* Hero, All-in-One focus */}
      <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 overflow-hidden bg-white" data-testid="homebuyer-hero">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-50 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-red-50 rounded-full blur-3xl opacity-40 translate-y-1/2 -translate-x-1/3 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-2 mb-8 animate-fade-in-up">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-xs font-bold tracking-[0.18em] text-green-700 uppercase">
                  Your team is ready
                </span>
              </div>

              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.02] tracking-tight mb-6 animate-fade-in-up animation-delay-100">
                Your homebuying journey,
                <span className="block text-[#D41F30]">simplified.</span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-10 max-w-xl mx-auto lg:mx-0 animate-fade-in-up animation-delay-200">
                Mortgage and real estate, all handled under one roof at CityWorth. No juggling multiple companies. No surprises. Just a simple, guided path from home searching to loan approval.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-400">
                <a
                  href={TEAM_PHONE_TEL}
                  className="inline-flex items-center justify-center bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-8 py-5 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  data-testid="homebuyer-hero-call"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Call your team
                </a>
                <a
                  href={TEAM_PHONE_SMS}
                  className="inline-flex items-center justify-center border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full px-8 py-5 text-lg font-semibold transition-all duration-300"
                  data-testid="homebuyer-hero-text"
                >
                  <MessageSquare className="mr-2 w-5 h-5" />
                  Text your team
                </a>
              </div>

              <div className="mt-6 text-center lg:text-left">
                <a href={TEAM_PHONE_TEL} className="text-xl font-display font-bold text-gray-900 hover:text-[#D41F30] transition-colors">
                  {TEAM_PHONE}
                </a>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 justify-center lg:justify-start text-sm text-gray-500 animate-fade-in-up animation-delay-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>No upfront cost</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Real expert on the phone</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Won't affect your credit</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 relative animate-fade-in-up animation-delay-300">
              <div className="relative w-full">
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="/img/25je1t4m.webp"
                    alt="Happy couple looking at homes for sale"
                    className="w-full h-[480px] object-cover"
                  />
                </div>

                <div className="hidden md:block absolute -top-5 -right-5 bg-white rounded-2xl shadow-2xl px-5 py-4 border border-gray-100 max-w-[230px]">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                      <Home className="w-4 h-4 text-[#D41F30]" />
                    </div>
                    <p className="font-bold text-gray-900 text-sm leading-tight">
                      Under one roof
                    </p>
                  </div>
                  <ul className="space-y-1.5 text-xs text-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      Lender
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      Real estate agent
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      Concierge support
                    </li>
                  </ul>
                </div>

                <div className="absolute -bottom-6 -left-4 md:-left-6 bg-white rounded-2xl shadow-xl p-5 border border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#D41F30] rounded-xl flex items-center justify-center">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-display text-3xl font-black text-gray-900 leading-none">21 days</p>
                      <p className="text-xs text-gray-500 mt-1">Average close time</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gray-50 border-b border-gray-100" data-testid="homebuyer-stats">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { value: "$1.7B+", label: "Loans Funded", icon: Building },
              { value: "10,000+", label: "Happy Customers", icon: Users },
              { value: "4.8/5", label: "Customer Rating", icon: Star },
            ].map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="w-14 h-14 mx-auto mb-4 bg-red-50 rounded-2xl flex items-center justify-center group-hover:bg-[#D41F30] transition-colors duration-300">
                  <stat.icon className="w-7 h-7 text-[#D41F30] group-hover:text-white transition-colors duration-300" />
                </div>
                <p className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">{stat.value}</p>
                <p className="text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="homebuyer-how-it-works" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-red-100 rounded-full px-4 py-2 mb-6">
              <Zap className="w-4 h-4 text-[#D41F30]" />
              <span className="text-sm font-medium text-[#D41F30]">Simple Process</span>
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How CityWorth Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From first click to keys in hand, we've streamlined every step
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", icon: Calculator, title: "Get Pre-Approved", description: "Quick online application takes just minutes. Know your budget instantly.", color: "bg-blue-500" },
              { step: "02", icon: Search, title: "Find Your Home", description: "Our agents help you discover properties that match your dreams.", color: "bg-green-500" },
              { step: "03", icon: FileText, title: "Lock Your Rate", description: "We secure the best rates and handle all the paperwork for you.", color: "bg-purple-500" },
              { step: "04", icon: Home, title: "Close & Celebrate", description: "Get your keys and move into your new home. We handle the rest.", color: "bg-[#D41F30]" },
            ].map((item, index) => (
              <div key={index} className="relative group">
                <div className="bg-white rounded-3xl p-8 h-full shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className={`w-14 h-14 ${item.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-6xl font-bold text-gray-100 absolute top-6 right-6">{item.step}</span>
                  <h3 className="font-playfair text-xl font-semibold text-gray-900 mb-3 relative">{item.title}</h3>
                  <p className="text-gray-600 relative">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={TEAM_PHONE_TEL}
              className="inline-flex items-center justify-center bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-8 py-5 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              data-testid="homebuyer-process-call"
            >
              <Phone className="mr-2 w-5 h-5" />
              Call your team
            </a>
            <a
              href={TEAM_PHONE_SMS}
              className="inline-flex items-center justify-center border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full px-8 py-5 text-base font-semibold transition-all duration-300"
              data-testid="homebuyer-process-text"
            >
              <MessageSquare className="mr-2 w-5 h-5" />
              Text your team
            </a>
          </div>
        </div>
      </section>

      {/* Why CityWorth */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-100 rounded-full px-4 py-2 mb-6">
                <Award className="w-4 h-4 text-[#D41F30]" />
                <span className="text-sm font-medium text-[#D41F30]">Why Choose Us</span>
              </div>
              <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                The CityWorth <span className="text-[#D41F30]">Advantage</span>
              </h2>
              <p className="text-xl text-gray-600 mb-10">
                We're not just another mortgage company. We're your partners in making homeownership simple, affordable, and stress-free.
              </p>

              <div className="space-y-6">
                {[
                  { icon: Users, title: "One Dedicated Team", description: "Your real estate agent and mortgage expert work together seamlessly" },
                  { icon: Zap, title: "Lightning Fast Process", description: "Close in as little as 21 days with our streamlined approach" },
                  { icon: Shield, title: "Transparent Pricing", description: "No hidden fees, no surprises. Know exactly what you're paying" },
                  { icon: Heart, title: "Personalized Guidance", description: "We walk you through every step, answering all your questions" },
                ].map((feature, index) => (
                  <div key={index} className="flex gap-4 p-4 rounded-2xl hover:bg-gray-50 transition-colors duration-300">
                    <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-[#D41F30]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img loading="lazy"
                  src="/img/pxyj2imd.webp"
                  alt="The CityWorth team working together to help families find homes"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6">
                    <div className="flex items-center gap-4">
                      <div className="flex -space-x-3">
                        <div className="w-10 h-10 bg-[#D41F30] rounded-full flex items-center justify-center text-white font-semibold">J</div>
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">S</div>
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold">M</div>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Join 10,000+ Happy Customers</p>
                        <p className="text-sm text-gray-500">Who made their dreams come true</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FamilyBanner testId="family-banner-homebuyer" />
      <ReviewsSection testId="homebuyer-reviews" />
    </>
  );
};


// ============================================
// START JOURNEY PAGE
// ============================================

const StartJourneyPage = () => {
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="bg-white min-h-screen">
      {!showForm ? (
        <>
          {/* Hero Section */}
          <section className="pt-32 pb-20 md:pt-40 md:pb-28" data-testid="start-journey-hero">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-red-100 rounded-full px-4 py-2 mb-6">
                    <Home className="w-4 h-4 text-[#D41F30]" />
                    <span className="text-sm font-medium text-[#D41F30]">Your Path to Homeownership</span>
                  </div>
                  
                  <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                    Ready to Become a <span className="text-[#D41F30]">Homeowner?</span>
                  </h1>
                  
                  <p className="text-lg text-gray-600 leading-relaxed mb-8">
                    Take the first step toward owning your dream home. Our all-in-one homebuying process makes it simple, stress-free, and supported every step of the way.
                  </p>
                  
                  <div className="space-y-4 mb-10">
                    {[
                      "Get matched with a dedicated real estate agent and mortgage expert",
                      "Understand your budget and explore loan options",
                      "Find your perfect home with personalized guidance",
                      "Close with confidence knowing you have a team behind you"
                    ].map((item, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{item}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-6 py-3.5 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    onClick={() => setShowForm(true)}
                    data-testid="start-journey-cta"
                  >
                    Start Homebuying Journey
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
                
                {/* Hero Image */}
                <div className="relative hidden lg:block">
                  <div className="rounded-3xl overflow-hidden shadow-2xl">
                    <img loading="lazy" 
                      src="/img/67a1kcm3.webp"
                      alt="Happy family in front of their new home"
                      className="w-full h-[500px] object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#D41F30] rounded-xl flex items-center justify-center">
                        <Star className="w-6 h-6 text-white fill-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">10,000+</p>
                        <p className="text-sm text-gray-500">Happy Homeowners</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Why Start Now Section */}
          <section className="py-20 bg-gray-50" data-testid="why-start-section">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="text-center mb-16">
                <h2 className="font-playfair text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Why Start Your Journey Today?
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Homeownership is one of life's biggest milestones. Here's why now is the perfect time to take that first step.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    icon: TrendingUp,
                    title: "Build Long-Term Wealth",
                    description: "Every mortgage payment builds equity in an asset that's yours. Start building wealth that grows over time.",
                    color: "bg-green-500"
                  },
                  {
                    icon: ShieldCheck,
                    title: "Create Stability",
                    description: "No more rent increases or uncertainty. Own your space and create a stable foundation for your future.",
                    color: "bg-blue-500"
                  },
                  {
                    icon: Heart,
                    title: "Make It Yours",
                    description: "Paint the walls, renovate the kitchen, build a life. Your home, your rules, your memories.",
                    color: "bg-[#D41F30]"
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 group">
                    <div className={`w-14 h-14 ${item.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <item.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-playfair text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
              
              <div className="text-center mt-12">
                <Button 
                  className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-6 py-3.5 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                  onClick={() => setShowForm(true)}
                  data-testid="why-start-cta"
                >
                  Start Homebuying Journey
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </section>

          {/* Simple Footer */}
          <footer className="py-10 bg-gray-900">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <img loading="lazy" 
                  src={LOGO_URL} 
                  alt="CityWorth" 
                  className="h-8 w-auto"
                />
                <div className="text-center md:text-right">
                  <p className="text-gray-400 text-sm">
                    CityWorth Mortgage LLC | NMLS# 925476
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    Equal Housing Lender
                  </p>
                </div>
              </div>
            </div>
          </footer>
        </>
      ) : (
        /* Custom Lead Form Section */
        <section className="min-h-screen bg-gray-50" data-testid="start-journey-form-section">
          <LeadForm />
          <div className="text-center pb-12">
            <button 
              onClick={() => setShowForm(false)}
              className="text-gray-500 hover:text-gray-700 text-sm underline"
            >
              ← Back to overview
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

// ============================================
// GET STARTED PAGE (Direct to form)
// ============================================

const GetStartedPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="Get Started | Your homebuying plan in 2 minutes"
        description="Tell us about your homebuying goals and get a personalized plan from CityWorth. 2 minutes, no credit check, no commitment."
        path="/get-started"
        noindex
      />
      <section className="min-h-screen bg-gray-50" data-testid="get-started-form-section">
        <LeadForm />
      </section>
      
      {/* Simple Footer */}
      <footer className="py-10 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img 
              src={LOGO_URL} 
              alt="CityWorth" 
              className="h-8 w-auto"
            />
            <div className="text-center md:text-right">
              <p className="text-gray-400 text-sm">
                CityWorth Mortgage LLC | NMLS# 925476
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Equal Housing Lender
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ============================================
// EMPLOYEE BENEFIT LANDING PAGE
// ============================================

const EmployeeBenefitPage = () => {
  return (
    <div className="bg-gradient-to-b from-white via-red-50/30 to-white">
      <SEO
        title="Employee Homebuying Benefit"
        description="Your company's homebuying benefit gives you a dedicated CityWorth team: lender, real estate agent, and concierge support, all under one roof at no upfront cost."
        path="/employee-benefit"
      />
      {/* Hero Section - What Happens Next */}
      <section className="pt-32 pb-20 md:pt-36 md:pb-24 bg-white" data-testid="eb-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Success Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 bg-green-100 border border-green-200 rounded-full px-5 py-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-700">Form Submitted Successfully</span>
            </div>
          </div>
          
          <div className="text-center mb-16">
            <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
              What Happens Next
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Now that you have started your journey, a member of your CityWorth homebuying team will be reaching out to learn more about your goals and help you take the next step.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: PhoneIncoming,
                title: "Connect With Your Team",
                description: "We will learn more about your goals, timeline, and where you are in the process.",
                color: "bg-blue-50",
                iconColor: "text-blue-600"
              },
              {
                icon: FileCheck,
                title: "Understand Your Options",
                description: "We will help you explore affordability, loan options, and the support available through your benefit.",
                color: "bg-red-50",
                iconColor: "text-[#D41F30]",
                hasLink: true
              },
              {
                icon: Home,
                title: "Move Forward With Confidence",
                description: "From pre-approval to home search to closing, CityWorth helps guide you every step of the way.",
                color: "bg-green-50",
                iconColor: "text-green-600"
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 p-8 group"
                data-testid={`eb-step-${index}`}
              >
                <div className={`w-14 h-14 rounded-xl ${item.color} flex items-center justify-center ${item.iconColor} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <item.icon className="w-7 h-7" />
                </div>
                <h3 className="font-playfair text-2xl font-medium text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{item.description}</p>
                {item.hasLink && (
                  <Button 
                    className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-6 py-3 text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-300"
                    onClick={() => window.open(PRE_APPROVAL_LINK, '_blank')}
                    data-testid="eb-preapproval-btn"
                  >
                    Get Pre-Approved
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          
          {/* Company Benefit Badge */}
          <div className="flex justify-center mt-12">
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-5 py-2">
              <Shield className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700 font-medium">Included through your company's homebuying benefit</span>
            </div>
          </div>
        </div>
      </section>

      {/* Employee Benefit Section */}
      <section className="py-24 bg-white" data-testid="eb-benefit">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-100 rounded-full px-4 py-2 mb-6">
                <Award className="w-4 h-4 text-[#D41F30]" />
                <span className="text-sm font-medium text-[#D41F30]">Employee Benefit</span>
              </div>
              <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                A Homebuying Benefit <span className="text-[#D41F30]">Built for You</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                Buying a home is one of the biggest financial goals many employees have. It can also feel confusing, expensive, and difficult to navigate alone. That is why your company offers access to CityWorth.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                This benefit was designed to give you real support as you work toward homeownership, with education, guidance, and a team that helps simplify the journey.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img loading="lazy" 
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Beautiful home ready for new owners"
                  className="w-full h-[400px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Company Benefit</p>
                    <p className="text-sm text-gray-500">Included with your employer</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: MessageCircle,
                title: "Guidance",
                description: "Get support from a team that helps you understand the process and your next steps.",
                color: "bg-blue-500"
              },
              {
                icon: Award,
                title: "Education",
                description: "Learn more about affordability, financing, and what it takes to become a homeowner.",
                color: "bg-green-500"
              },
              {
                icon: Zap,
                title: "Access",
                description: "Use your company's homebuying benefit to connect with professionals and resources in one place.",
                color: "bg-amber-500"
              }
            ].map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-3xl p-8 hover:bg-red-50/50 transition-all duration-300 group" data-testid={`eb-benefit-${index}`}>
                <div className={`w-14 h-14 ${item.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-playfair text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All-in-One Process Section - Enhanced Visual Design */}
      <section className="py-24 bg-white" data-testid="eb-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-red-100 rounded-full px-4 py-2 mb-6">
              <Zap className="w-4 h-4 text-[#D41F30]" />
              <span className="text-sm font-medium text-[#D41F30]">All-in-One Experience</span>
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              The All-in-One Homebuying Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              One of the hardest parts of buying a home is trying to coordinate everything on your own. CityWorth brings the key parts of the journey together in one connected experience.
            </p>
          </div>
          
          {/* Process Steps - Visual Timeline */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
            {[
              {
                step: "01",
                icon: Search,
                title: "Real Estate",
                description: "Get help searching for homes, scheduling tours, understanding the market, and making a strong offer.",
                color: "bg-blue-500"
              },
              {
                step: "02",
                icon: Calculator,
                title: "Mortgage",
                description: "Understand what you may qualify for, review loan options, and move toward pre-approval with guidance.",
                color: "bg-green-500"
              },
              {
                step: "03",
                icon: Shield,
                title: "Insurance",
                description: "Get connected to home insurance support as part of the overall process.",
                color: "bg-purple-500"
              },
              {
                step: "04",
                icon: Users,
                title: "One Team",
                description: "Instead of juggling different people on your own, CityWorth connects the process from start to finish.",
                color: "bg-[#D41F30]"
              }
            ].map((item, index) => (
              <div key={index} className="relative group" data-testid={`eb-process-${index}`}>
                <div className="bg-white rounded-3xl p-8 h-full shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className={`w-14 h-14 ${item.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-6xl font-bold text-gray-100 absolute top-6 right-6">{item.step}</span>
                  <h3 className="font-playfair text-xl font-semibold text-gray-900 mb-3 relative">{item.title}</h3>
                  <p className="text-gray-600 relative leading-relaxed">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Highlighted Benefits Bar */}
          <div className="bg-gradient-to-r from-red-50 to-red-100/50 rounded-2xl p-8">
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <span className="text-gray-700 font-medium">Less confusion</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <span className="text-gray-700 font-medium">Less back and forth</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <span className="text-gray-700 font-medium">More support in one place</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Homeownership Matters Section */}
      <section className="py-20 bg-gradient-to-b from-red-50/30 to-white" data-testid="eb-why-homeownership">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
            <div className="order-2 lg:order-1">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img loading="lazy" 
                  src="/img/hc6jfej9.webp"
                  alt="Happy family celebrating in front of new home with sold sign"
                  className="w-full h-[350px] object-cover"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="font-playfair text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Homeownership Matters
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                Homeownership is more than buying a house. It is the opportunity to create stability, put down roots, build equity over time, and move toward something that is yours.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                For many people, owning a home is one of life's biggest milestones. This benefit helps make that path feel clearer, more supported, and more achievable.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: ShieldCheck, label: "Stability" },
              { icon: Home, label: "A Place That Feels Like Yours" },
              { icon: TrendingUp, label: "Long-Term Wealth Building" },
              { icon: Star, label: "A Big Step Forward" }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300" data-testid={`eb-highlight-${index}`}>
                <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-[#D41F30]" />
                </div>
                <p className="font-medium text-gray-900">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pre-Approval CTA Section */}
      <section className="py-20 bg-white" data-testid="eb-preapproval-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-gradient-to-br from-[#D41F30] to-red-600 rounded-3xl p-10 md:p-14 text-center relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            
            <div className="relative">
              <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-4">
                Take the Next Step With Pre-Approval
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
                Getting pre-approved is one of the best ways to understand your budget, strengthen your plan, and move forward with confidence.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <Button 
                  className="bg-white text-[#D41F30] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                  onClick={() => window.open(PRE_APPROVAL_LINK, '_blank')}
                  data-testid="eb-preapproval-cta-primary"
                >
                  Get Pre-Approved
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300"
                  onClick={() => window.open(PRE_APPROVAL_LINK, '_blank')}
                  data-testid="eb-preapproval-cta-secondary"
                >
                  See How Much I Can Afford
                </Button>
              </div>
              
              <p className="text-white/80 text-sm">
                Your CityWorth team will help guide you through the next steps.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50" data-testid="eb-faq">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {[
              {
                question: "Will someone from CityWorth contact me?",
                answer: "Yes. A member of your CityWorth homebuying team will be reaching out to learn more about your goals and help guide you through the process."
              },
              {
                question: "Do I need to be ready to buy right now?",
                answer: "No. This benefit is here to help whether you are ready now or just starting to explore your options."
              },
              {
                question: "What does pre-approval help with?",
                answer: "Pre-approval can help you understand what you may qualify for, what price range may fit your budget, and what next steps make the most sense."
              },
              {
                question: "What makes CityWorth different?",
                answer: "CityWorth offers an all-in-one homebuying experience that helps connect real estate, mortgage, and insurance support in one coordinated process."
              }
            ].map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`eb-faq-${index}`}
                className="bg-white rounded-2xl border border-gray-100 px-6 shadow-sm"
                data-testid={`eb-faq-item-${index}`}
              >
                <AccordionTrigger className="text-left text-lg font-medium text-gray-900 hover:text-[#D41F30] transition-colors py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-6 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="py-20 bg-white" data-testid="eb-final-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Your Homebuying Journey Is Underway
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Your company's homebuying benefit is here to help you move forward with more clarity, more support, and a team that is ready to help.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              onClick={() => window.open(PRE_APPROVAL_LINK, '_blank')}
              data-testid="eb-final-cta-primary"
            >
              Get Pre-Approved
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <button 
              onClick={() => window.open(PRE_APPROVAL_LINK, '_blank')}
              className="text-[#D41F30] hover:text-[#B91C2A] font-medium underline underline-offset-4 transition-colors"
              data-testid="eb-final-cta-secondary"
            >
              See How Much I Can Afford
            </button>
          </div>
        </div>
      </section>

      {/* Simple Footer for Employee Benefit Page */}
      <footer className="py-10 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img loading="lazy" 
              src={LOGO_URL} 
              alt="CityWorth" 
              className="h-8 w-auto"
            />
            <div className="text-center md:text-right">
              <p className="text-gray-400 text-sm">
                CityWorth Mortgage LLC | NMLS# 925476
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Equal Housing Lender
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ============================================
// MAIN APP WITH ROUTING
// ============================================

// Fires FollowUpBoss Widget Tracker pageview on SPA route changes
function FUBPageviewTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.widgetTracker === "function") {
      window.widgetTracker("send", "pageview");
    }
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  return (
    <div className="App" data-testid="app">
      <BrowserRouter>
        <FUBPageviewTracker />
        <LeadPopup />
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

function AppRouter() {
  return (
    <Routes>
      {/* Auth + admin (no public header/footer) */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/admin" element={<AdminDashboardPage />} />
      <Route path="/admin/articles/:id" element={<AdminArticleEditorPage />} />

      {/* Start Journey page - with header but own footer */}
      <Route path="/start-journey" element={
        <>
          <MainHeader />
          <StartJourneyPage />
        </>
      } />

      {/* Get Started page - direct to form */}
      <Route path="/get-started" element={
        <>
          <MainHeader />
          <GetStartedPage />
        </>
      } />

      {/* Refinance thank-you (no footer) */}
      <Route path="/refinance-thanks" element={
        <>
          <MainHeader />
          <RefinanceThanksPage />
        </>
      } />

      {/* Employee Benefit page - with header but own footer */}
      <Route path="/employee-benefit" element={
        <>
          <MainHeader />
          <EmployeeBenefitPage />
        </>
      } />
      <Route path="/benefits" element={
        <>
          <MainHeader />
          <EmployeeBenefitPage />
        </>
      } />

      {/* Main site pages with shared header/footer */}
      <Route path="*" element={
        <>
          <MainHeader />
          <main>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/homebuyer" element={<HomebuyerLandingPage />} />
              <Route path="/buy-a-home" element={<BuyAHomePage />} />
              <Route path="/mortgage-calculator" element={<MortgageCalculatorPage />} />
              <Route path="/first-time-homebuyer-guide" element={<FirstTimeHomebuyerGuidePage />} />
              <Route path="/real-estate" element={<RealEstatePage />} />
              <Route path="/refinance" element={<RefinancePage />} />
              <Route path="/loans/:slug" element={<LoanProgramPage />} />
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              {/* Loan officer profile pages (e.g. /arizzi). Unknown slugs redirect home inside the page. */}
              <Route path="/:officerSlug" element={<LoanOfficerPage />} />
              {/* Catch-all: redirect any unknown URL (e.g. /principal) to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
        </>
      } />
    </Routes>
  );
}

export default App;
